import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {MapPin, User} from 'lucide-react';
import {cn} from '@chekinapp/ui';
import {countNights, formatDisplayDate} from '@chekin/shared/guest-form';
import {measurePropertyHeaderFrame, useArrivalProperty} from 'components/arrival';
import genericPhoto from 'assets/img/property-link-generic.webp';
import type {FoundReservation} from '../../useBookingFoundHandoffStore';
import styles from './BookingFoundScreen.module.css';

const STAY_DATE: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
};

function toFirstName(fullName?: string) {
  const first = fullName?.trim().split(/\s+/)[0];
  if (!first) return '';

  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
}

type Props = {
  reservation: FoundReservation;
  guestName?: string;
  propertyName?: string;
  isNew?: boolean;
  isLeaving?: boolean;
};

function BookingFoundScreen({
  reservation,
  guestName,
  propertyName,
  isNew,
  isLeaving,
}: Props) {
  const {t, i18n} = useTranslation();
  const liveProperty = useArrivalProperty();
  const [property] = useState(() => ({name: liveProperty.name, city: liveProperty.city}));
  const [headerFrame] = useState(measurePropertyHeaderFrame);
  const firstName = toFirstName(guestName);

  const nights = countNights(reservation.check_in_date, reservation.check_out_date);
  const guests =
    reservation.guest_group?.number_of_guests ??
    reservation.guest_group?.known_number_of_guests;
  const location = [propertyName || property.name, property.city]
    .filter(Boolean)
    .join(', ');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="booking-found-title"
      aria-live="polite"
      className={cn(styles.screen, isLeaving && styles.leaving)}
      style={headerFrame}
    >
      <div className={styles.scene}>
        <img src={genericPhoto} alt="" className={styles.photo} />

        <div className="relative z-[2] flex flex-1 flex-col items-center justify-center p-6 text-center">
          <svg viewBox="0 0 52 52" aria-hidden="true" className={styles.mark}>
            <circle className={styles.circle} cx="26" cy="26" r="24" />
            <g className={styles.glass}>
              <circle cx="24" cy="24" r="7.5" />
              <path d="m29.5 29.5 6 6" />
            </g>
            <path
              className={styles.house}
              d="M15 26 26 17l11 9v10.5a1.5 1.5 0 0 1-1.5 1.5h-19A1.5 1.5 0 0 1 15 36.5z"
            />
            <path className={styles.door} d="M22.5 38v-6.5h7V38" />
          </svg>

          <p
            className={cn(
              styles.eyebrow,
              'mb-1.5 text-[12px] font-bold uppercase tracking-[0.1em] text-[color:var(--chekin-on-scene-text-eyebrow)]',
            )}
          >
            {isNew
              ? t('arrival_booking_created_eyebrow')
              : t('arrival_booking_found_eyebrow')}
          </p>
          <h2
            id="booking-found-title"
            className={cn(
              styles.title,
              'text-[32px] font-bold leading-[1.15] tracking-[-0.02em] desk:text-[46px]',
            )}
          >
            {firstName
              ? t('welcome_named', {name: firstName})
              : t('arrival_welcome_plain')}
          </h2>

          <div
            className={cn(
              styles.card,
              'mt-[22px] w-full max-w-[380px] rounded-[18px] bg-white/[0.16] p-4 text-left desk:max-w-[440px]',
            )}
          >
            <div className="mb-3 flex justify-between gap-3 border-b border-white/20 pb-3">
              <div>
                <small className="block text-[12px] font-bold uppercase tracking-[0.08em] text-[color:var(--chekin-on-scene-text-eyebrow)]">
                  {t('check_in_short')}
                </small>
                <strong className="block text-[16px] font-bold">
                  {formatDisplayDate(reservation.check_in_date, i18n.language, STAY_DATE)}
                </strong>
              </div>
              {nights !== null && (
                <span className="self-center rounded-full bg-white/20 px-2.5 py-[3px] text-[12px] font-semibold">
                  {t('night_count', {count: nights})}
                </span>
              )}
              <div className="text-right">
                <small className="block text-[12px] font-bold uppercase tracking-[0.08em] text-[color:var(--chekin-on-scene-text-eyebrow)]">
                  {t('check_out_short')}
                </small>
                <strong className="block text-[16px] font-bold">
                  {formatDisplayDate(
                    reservation.check_out_date,
                    i18n.language,
                    STAY_DATE,
                  )}
                </strong>
              </div>
            </div>
            {location && (
              <div className="flex items-center gap-2 text-[14px] text-[color:var(--chekin-on-scene-text-meta)]">
                <MapPin size={16} className="shrink-0" />
                {location}
              </div>
            )}
            {guests ? (
              <div className="mt-1.5 flex items-center gap-2 text-[14px] text-[color:var(--chekin-on-scene-text-meta)]">
                <User size={16} className="shrink-0" />
                {t('guest_count', {count: guests})}
              </div>
            ) : null}
          </div>

          <p
            className={cn(
              styles.opening,
              'mt-5 flex items-center gap-2 text-[14px] font-semibold text-[color:var(--chekin-on-scene-text-eyebrow)]',
            )}
          >
            {t('arrival_opening_your_booking')}
            <i
              className={cn(
                styles.progress,
                'relative block h-[3px] w-14 overflow-hidden rounded-full bg-white/20',
              )}
            />
          </p>
        </div>
      </div>
    </div>
  );
}

export {BookingFoundScreen};
